// Planning-view task grouping helpers.
//
// The Planning table is flat: rows load in backend order with no visible
// hierarchy even when the left tree has narrowed the set to a building /
// category / etc. This module reorders those rows so same-group tasks sit
// together and flags the first row of each group so the table can paint a
// thick divider. Nothing else — no headers, no collapse, no selector.
//
// The axis is derived silently from the tree selection (TaskFilters).
// Planning uses snake_case field names, so the helper is Planning-specific
// and doesn't share code with the admin Tasks view's grouping module.

import type { Task, TaskFilters } from '../shared/types';

export type PlanningGroupAxis =
    | 'none'          // no grouping — e.g., tree drilled to a Unit
    | 'building'
    | 'category'
    | 'subCategory'
    | 'floor'
    | 'unit';

/**
 * Axis rule: Floor is the default slice in the Planning task table.
 * - Tree drilled to a specific Unit → 'none' (flat; nothing left to slice).
 * - Tree drilled to a specific Floor → 'unit' (one step deeper).
 * - Everything else (root, Building, Category, Subcategory) → 'floor'.
 *
 * The decision was made deliberately simple because Floor and Unit now lead
 * the table as the first two visible columns — grouping on anything other
 * than Floor/Unit would make those leading columns thrash mid-group.
 */
export function computeAutoGroupAxis(
    filters: TaskFilters | null,
): PlanningGroupAxis {
    if (!filters)         return 'floor';
    if (filters.unit_id)  return 'none';
    if (filters.floor_id) return 'unit';
    return 'floor';
}

const NO_KEY = '__none__';

/** Group key for a single task on the chosen axis. */
export function groupKeyForTask(task: Task, axis: PlanningGroupAxis): string {
    switch (axis) {
        case 'none':        return NO_KEY;
        case 'building':    return task.building    ?? NO_KEY;
        case 'category':    return task.category    ?? NO_KEY;
        case 'subCategory': return task.subCategory ?? NO_KEY;
        case 'floor':       return task.floor       ?? NO_KEY;
        case 'unit':        return task.unit        ?? NO_KEY;
    }
}

/**
 * Reorder tasks so rows sharing a group key sit together, and return the set
 * of task ids that are the first row of their group (the table paints the
 * thick top-border on those ids).
 *
 * - Uses a stable sort so tasks within a group keep the backend's order,
 *   which already encodes meaningful sequencing (e.g., WBS code).
 * - When axis === 'none', returns the input untouched with an empty set,
 *   so the caller renders exactly as before.
 */
export function orderTasksByGroup(
    tasks: Task[],
    axis: PlanningGroupAxis,
): { tasks: Task[]; firstOfGroup: Set<string> } {
    if (axis === 'none' || tasks.length === 0) {
        return { tasks, firstOfGroup: new Set() };
    }

    // Array.prototype.sort is stable in modern engines; compare by group
    // key only so within-group order is preserved.
    const keyed = tasks.map((t) => ({ task: t, key: groupKeyForTask(t, axis) }));
    keyed.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));

    const ordered = keyed.map((k) => k.task);
    const firstOfGroup = new Set<string>();
    let prevKey: string | null = null;
    for (const { task, key } of keyed) {
        if (key !== prevKey) {
            firstOfGroup.add(task.id);
            prevKey = key;
        }
    }
    return { tasks: ordered, firstOfGroup };
}
